// The tools of the maps that draw delivery zones (DeliveryZonesMap, AreaMap), driven through geoman's API by our own
// toolbar buttons instead of geoman's toolbar. One tool at a time, like geoman's toolbar did. No Leaflet import, so
// the tests can use a fake map.pm.

/** Draw a polygon or a circle, edit the zones (move their points) or cut a piece out of a zone */
export type ZoneTool = 'Polygon' | 'Circle' | 'Edit' | 'Cut'

/** The part of geoman's map.pm the tools use */
export interface ZoneToolsPM {
    Draw: {getActiveShape(): string | undefined}
    globalEditModeEnabled(): boolean
    enableDraw(shape: 'Polygon' | 'Circle'): void
    /** Stops drawing any shape, cutting included (geoman's Draw.disable disables all shapes) */
    disableDraw(): void
    enableGlobalEditMode(): void
    disableGlobalEditMode(): void
    enableGlobalCutMode(options?: object): void
}

// What geoman's toolbar passed to the cut tool
const cutOptions = {snappable: true, cursorMarker: true, allowSelfIntersection: false}

/** The tool that is on, null when the map just pans */
export function activeZoneTool(pm: ZoneToolsPM): ZoneTool | null {
    if (pm.globalEditModeEnabled()) {
        return 'Edit'
    }

    const shape = pm.Draw.getActiveShape()
    return shape === 'Polygon' || shape === 'Circle' || shape === 'Cut' ? shape : null
}

/** Switches off the tool that is on and switches on tool; null switches all tools off */
export function setZoneTool(pm: ZoneToolsPM, tool: ZoneTool | null) {
    if (pm.Draw.getActiveShape()) {
        pm.disableDraw()
    }

    if (pm.globalEditModeEnabled()) {
        pm.disableGlobalEditMode()
    }

    if (tool === 'Edit') {
        pm.enableGlobalEditMode()
    } else if (tool === 'Cut') {
        pm.enableGlobalCutMode(cutOptions)
    } else if (tool) {
        pm.enableDraw(tool)
    }
}

/** A toolbar button: switches its tool on, or off when it is on already */
export function toggleZoneTool(pm: ZoneToolsPM, tool: ZoneTool) {
    setZoneTool(pm, activeZoneTool(pm) === tool ? null : tool)
}

/** Whether the tool draws point by point, so its last point can be taken back */
export function drawsPoints(tool: ZoneTool | null): tool is 'Polygon' | 'Cut' {
    return tool === 'Polygon' || tool === 'Cut'
}

/** Takes back the last point of the polygon or cut being drawn (geoman's 'Laatste punt verwijderen' action) */
export function removeLastPoint(pm: ZoneToolsPM) {
    const tool = activeZoneTool(pm)

    if (drawsPoints(tool)) {
        // Not in geoman's types: the method its own toolbar action calls
        const shapes = pm.Draw as unknown as Record<string, {_removeLastVertex?: () => void} | undefined>
        shapes[tool]?._removeLastVertex?.()
    }
}
